import { useEffect, useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Users, Target, Layers, Plug, ShieldCheck } from 'lucide-react';
import { BrandHero, SectionHeader, CheckList, CtaBand } from '@/components/brand/BrandKit';
import { SimpleLeadForm } from '@/components/forms/SimpleLeadForm';
import { PARTNERSHIPS, PARTNERSHIP_ORDER, COLLABORATE_DISCLOSURE } from '@/data/partnerships';

function InfoCard({ icon: Icon, title, items }) {
  return (
    <div className="card-light p-6">
      <span className="grid h-11 w-11 place-items-center rounded-lg bg-[#EE5A01] text-white"><Icon className="w-5 h-5" /></span>
      <h3 className="mt-4 font-heading font-bold text-base text-[#121212]">{title}</h3>
      <ul className="mt-4 space-y-2.5">
        {items.map((t) => (
          <li key={t} className="flex items-start gap-2.5 font-body text-sm text-[#555555] leading-relaxed"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#EE5A01] flex-shrink-0" />{t}</li>
        ))}
      </ul>
    </div>
  );
}

function JourneyDiagram({ steps }) {
  return (
    <div data-testid="partner-journey" className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4">
      {steps.map((s, i) => (
        <div key={s} className="relative rounded-2xl border border-black/5 bg-white p-5 text-center">
          <span className="mx-auto grid h-11 w-11 place-items-center rounded-full bg-[#FFF4ED] font-heading font-black text-[#EE5A01]">{String(i + 1).padStart(2, '0')}</span>
          <p className="mt-3 font-heading font-semibold text-sm text-[#121212] leading-snug">{s}</p>
          {i < steps.length - 1 && <ArrowRight className="hidden lg:block absolute top-1/2 -right-3 -translate-y-1/2 w-5 h-5 text-[#EE5A01]/40" />}
        </div>
      ))}
    </div>
  );
}

export default function PartnershipPage() {
  const { slug } = useParams();
  const p = PARTNERSHIPS[slug];

  useEffect(() => { if (p) document.title = `${p.title} | Collaborate | Eurogulf Mobility Group`; }, [p]);
  const otherPathways = useMemo(() => PARTNERSHIP_ORDER.filter((s) => s !== slug), [slug]);

  if (!p) return <Navigate to="/collaborate" replace />;

  return (
    <div data-testid="partnership-page">
      <BrandHero testid="partnership-hero" chip="Collaborate With Us" title={p.title} lead={p.lead} image={p.image} position="center 60%"
        ctas={[{ label: 'Start a Partnership', href: '#enquiry', testid: 'partnership-hero-cta' }, { label: 'All Pathways', href: '/collaborate', variant: 'outline' }]} />

      <section className="bg-white pt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/collaborate" className="inline-flex items-center gap-1.5 font-heading font-bold text-xs text-[#666666] hover:text-[#EE5A01] transition-colors"><ArrowLeft className="w-4 h-4" /> Back to all pathways</Link>
        </div>
      </section>

      <section className="bg-white py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <InfoCard icon={Users} title="Who this is for" items={p.whoFor} />
          <InfoCard icon={Target} title="What it solves" items={p.problems} />
        </div>
      </section>

      <section className="bg-[#FAFAFA] py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <InfoCard icon={Layers} title="Available product combinations" items={p.products} />
          <InfoCard icon={Plug} title="Integration & booking possibilities" items={p.integrations} />
        </div>
      </section>

      <section className="bg-white py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader chip="Partner Journey" title="How the partnership comes together." align="center" />
          <div className="mt-12"><JourneyDiagram steps={p.journey} /></div>
        </div>
      </section>

      <section className="bg-[#EEEDE7] py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader chip="Case-Study Framework" title={p.caseStudyLabel} desc="An illustrative framework for how we scope, deliver and measure this type of partnership." />
          <div className="mt-8 rounded-2xl border border-black/5 bg-white p-6 sm:p-8">
            <CheckList items={p.caseStudy} />
            <p className="mt-6 font-body text-xs text-[#888] leading-relaxed">Illustrative framework only. Real outcomes are defined jointly with each partner and confirmed under a signed agreement.</p>
          </div>
        </div>
      </section>

      <section id="enquiry" className="bg-white py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <SectionHeader chip="Start the Conversation" title="Tell us about your opportunity." desc="Share a few details and our partnerships team will respond with a tailored proposal." />
            <div className="mt-6 flex items-start gap-3 rounded-2xl border border-black/5 bg-[#FAFAFA] p-5">
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-[#FFF4ED] text-[#EE5A01] flex-shrink-0"><ShieldCheck className="w-4 h-4" /></span>
              <p className="font-body text-xs text-[#666666] leading-relaxed">{COLLABORATE_DISCLOSURE}</p>
            </div>
          </div>
          <div className="lg:col-span-6">
            <SimpleLeadForm enquiryType={`Partnership: ${p.title}`} source={p.title} title={`${p.title} enquiry`} testid="partnership-lead-form" />
          </div>
        </div>
      </section>

      <section className="bg-[#FAFAFA] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-heading font-bold text-[11px] tracking-[0.2em] uppercase text-[#666666] mb-4">Other partnership pathways</p>
          <div className="flex flex-wrap gap-2">
            {otherPathways.map((s) => (
              <Link key={s} to={`/collaborate/${s}`} className="rounded-full border border-black/10 bg-white px-4 py-2 font-heading font-semibold text-xs text-[#121212] hover:border-[#EE5A01] hover:text-[#EE5A01] transition-colors">{PARTNERSHIPS[s].title}</Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBand title="Let's build something that moves your business forward." ctas={[{ label: 'Contact Our Team', href: '/contact', testid: 'partnership-cta' }, { label: 'Call 800 364', href: 'tel:800364', external: true, variant: 'outline' }]} />
    </div>
  );
}
